import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_CONFIG } from '../config/stripe';
import { useAuth } from '../contexts/AuthContext';

const StripeTest: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  const addDebugInfo = (info: string) => {
    setDebugInfo(prev => [...prev, `${new Date().toISOString()}: ${info}`]);
    console.log(info);
  };

  const testStripeCheckout = async () => {
    setLoading(true);
    setDebugInfo([]);
    
    try {
      addDebugInfo('Starting Stripe test...');
      addDebugInfo(`Publishable Key: ${STRIPE_CONFIG.publishableKey?.substring(0, 20)}...`);
      addDebugInfo(`Monthly Price ID: ${STRIPE_CONFIG.prices.pro_monthly}`);
      
      // First try backend API
      addDebugInfo('Calling backend API to create checkout session...');
      
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: STRIPE_CONFIG.prices.pro_monthly,
          userId: user?.uid || 'test-user',
          userEmail: user?.email || undefined,
          planType: 'pro_monthly',
          successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/payment-cancel`,
        }),
      });
      
      addDebugInfo(`API Response Status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        addDebugInfo(`Session created: ${data.sessionId ? 'Success' : 'Failed'}`);
        
        if (data.sessionUrl) {
          addDebugInfo('Redirecting to Stripe Checkout...');
          window.location.href = data.sessionUrl;
        } else if (data.sessionId) {
          // Initialize Stripe and redirect
          addDebugInfo('Using session ID to redirect...');
          const stripe = await loadStripe(STRIPE_CONFIG.publishableKey);
          
          if (!stripe) {
            addDebugInfo('ERROR: Stripe failed to initialize');
            return;
          }
          
          const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
          if (error) {
            addDebugInfo(`ERROR: ${error.message}`);
          }
        }
      } else {
        const errorData = await response.text();
        addDebugInfo(`API Error: ${errorData}`);
      }
    } catch (err: any) {
      addDebugInfo(`EXCEPTION: ${err.message}`);
      console.error('Exception:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">Stripe Integration Test</h1>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-4">
          <h2 className="text-lg font-semibold mb-2">Configuration</h2>
          <div className="space-y-2 text-sm font-mono">
            <div>Publishable Key: {STRIPE_CONFIG.publishableKey?.substring(0, 30)}...</div>
            <div>Monthly Price: {STRIPE_CONFIG.prices.pro_monthly}</div>
            <div>Yearly Price: {STRIPE_CONFIG.prices.pro_yearly}</div>
            <div>User Email: {user?.email || 'Not signed in'}</div>
          </div>
        </div>
        
        <button
          onClick={testStripeCheckout}
          disabled={loading}
          className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Testing...' : 'Test Stripe Checkout'}
        </button>
        
        {debugInfo.length > 0 && (
          <div className="mt-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
            <h3 className="font-semibold mb-2">Debug Log:</h3>
            <div className="space-y-1 text-sm font-mono">
              {debugInfo.map((info, index) => (
                <div key={index} className={info.includes('ERROR') ? 'text-red-600' : ''}>
                  {info}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="font-semibold mb-2">Test Instructions:</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm">
            <li>Click "Test Stripe Checkout" button</li>
            <li>Check the debug log for any errors</li>
            <li>If successful, you'll be redirected to Stripe Checkout</li>
            <li>Use test card: 4242 4242 4242 4242</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default StripeTest;